import React, { useEffect } from "react";
import { Modal, Spinner, Button } from "react-bootstrap";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  fetchEmployeeById,
  fetchUserById,
  fetchCompanyById,
} from "../../store/slices/userSlice";
import user_img_1 from "../../assets/images//User.png";
import { formatDate } from "../../utils/dateUtils";
import { maskAccountDetail } from "../../utils/maskUtils";

const ProfileDetail = ({ show, onClose, type, clientId }) => {
  const dispatch = useAppDispatch();

  const MODAL_KEY = `profileModal_${clientId}`;

  // Selectors
  const selectedUser = useAppSelector(
    (s) => s.user.entityByKey[MODAL_KEY] || null,
  );
  const loading = useAppSelector(
    (s) => s.user.entityLoadingByKey[MODAL_KEY] || false,
  );
  const error = useAppSelector(
    (s) => s.user.entityErrorByKey[MODAL_KEY] || null,
  ); // Corrected key

  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

  const fetchData = () => {
    if (clientId) {
      if (type === "employee")
        dispatch(fetchEmployeeById({ id: clientId, key: MODAL_KEY }));
      else if (type === "company")
        dispatch(fetchCompanyById({ id: clientId, key: MODAL_KEY }));
      else dispatch(fetchUserById({ id: clientId, key: MODAL_KEY }));
    }
  };

  useEffect(() => {
    if (show) {
      fetchData();
    }
  }, [show, clientId, type, dispatch, MODAL_KEY]);

  if (!show) return null;

  // Build full address from available address fields
  const buildFullAddress = (user) => {
    if (!user) return "N/A";
    const addressLine =
      user.addressLine || user.addressLine1 || user.address || "";
    const parts = [
      addressLine,
      user.city,
      user.state,
      user.postalCode,
      user.country,
    ];
    return parts.filter(Boolean).join(", ") || "N/A";
  };

  // Data mapping logic
  const client = selectedUser
    ? {
        avatar: selectedUser.profile
          ? BASE_URL + selectedUser.profile
          : user_img_1,
        name: selectedUser.name || "N/A",
        email: selectedUser.email || "N/A",
        status: selectedUser.isActive ? "Active" : "Inactive",
        role: selectedUser.roleName || "N/A",
        phone: selectedUser.phone || "N/A",
        country: selectedUser.country || "N/A",
        state: selectedUser.state || "N/A",
        city: selectedUser.city || "N/A",
        zip: selectedUser.postalCode || "N/A",
        address: buildFullAddress(selectedUser),
        payrollCycle: selectedUser.payrollCycle,
        payrollDate: formatDate(selectedUser.payrollDate),
        tax: selectedUser.formulas?.map((f) => f.code).join(", ") || "",
        bankName: selectedUser.bankName,
        accountHolder: maskAccountDetail(selectedUser.bankHolderName) || "N/A",
        accountNo: maskAccountDetail(selectedUser.accountNumber),
        routingNo: maskAccountDetail(selectedUser.routingNumber),
        department: selectedUser.department,
        designation: selectedUser.designation,
        ctc: "-",
        totalPay: selectedUser.totalPay,
        // Client info for company type
        clientName: selectedUser.clientName || "",
        clientEmail: selectedUser.clientEmail || "",
        companyName: selectedUser.companyName || "",
      }
    : null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="profile_detail p-0 m-0"
    >
      <Modal.Body className="p-0 m-0">
        {/* 1. LOADER STATE */}
        {loading ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "350px" }}
          >
            <Spinner animation="border" variant="primary" role="status" />
            <p className="mt-2 text-muted">Loading profile details</p>
          </div>
        ) : error ? (
          /* 2. ERROR STATE */
          <div
            className="d-flex flex-column justify-content-center align-items-center p-5 text-center"
            style={{ minHeight: "350px" }}
          >
            <div className="text-danger mb-3">
              <i
                className="bi bi-exclamation-triangle-fill"
                style={{ fontSize: "2rem" }}
              ></i>
            </div>
            <h5 className="text-dark">Oops! Something went wrong</h5>
            <p className="text-muted mb-4">{error}</p>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={onClose}>
                Close
              </Button>
              <Button variant="primary" onClick={fetchData}>
                Try Again
              </Button>
            </div>
          </div>
        ) : !client ? (
          /* 3. NO DATA STATE */
          <div className="p-5 text-center">No details available.</div>
        ) : (
          /* 4. SUCCESS STATE (Main UI) */
          <div className="client-profile-card mt-0 mb-0">
            <div className="profile-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                {type === "employee"
                  ? "Employee"
                  : type === "company"
                    ? "Company"
                    : "Client"}{" "}
                Details
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>

            <div className="client-info-header d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <img
                  src={client.avatar || "/default-avatar.png"}
                  alt="Client"
                  className="user-avatar"
                />
                <div>
                  <div className="fw-bold">{client.name}</div>
                  <small className="text-muted">{client.email}</small>
                </div>
              </div>
              <div className={`status-badge ${client.status.toLowerCase()}`}>
                <span className="status-dot"></span> {client.status}
              </div>
            </div>

            <div className="info-section">
              <h6 className="section-title">Basic Info</h6>
              <div className="row">
                <div className="col-md-4 col-6 info-item">
                  <label>Phone Number</label>
                  <p>{client.phone}</p>
                </div>
                <div className="col-md-4 col-6 info-item">
                  <label>Country</label>
                  <p>{client.country}</p>
                </div>
                <div className="col-md-4 col-6 info-item">
                  <label>State</label>
                  <p>{client.state}</p>
                </div>
                <div className="col-md-4 col-6 info-item">
                  <label>City</label>
                  <p>{client.city}</p>
                </div>
                <div className="col-md-4 col-6 info-item">
                  <label>ZIP Code</label>
                  <p>{client.zip}</p>
                </div>
                <div className="col-md-4 col-6 info-item">
                  <label>Address</label>
                  <p>{client.address}</p>
                </div>
                {type === "employee" && (
                  <>
                    <div className="col-md-4 col-6 info-item">
                      <label>Designation</label>
                      <p>{client.designation}</p>
                    </div>
                    <div className="col-md-4 col-6 info-item">
                      <label>Department</label>
                      <p>{client.department}</p>
                    </div>
                    <div className="col-md-4 col-6 info-item">
                      <label>Annual CTC</label>
                      <p>{client.ctc}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {type === "employee" && (
              <div className="info-section">
                <h6 className="section-title">Salary & Bank Info</h6>
                <div className="row">
                  <>
                    <div className="col-md-4 col-6 info-item">
                      <label>Total Pay</label>
                      <p>{client.totalPay || "N/A"}</p>
                    </div>
                  </>
                </div>
                <div className="row">
                  <div className="col-md-4 col-6 info-item">
                    <label>Holder Name</label>
                    <p>{client.accountHolder}</p>
                  </div>
                  <div className="col-md-4 col-6 info-item">
                    <label>Account No.</label>
                    <p>{client.accountNo}</p>
                  </div>
                  <div className="col-md-4 col-6 info-item">
                    <label>Routing Number</label>
                    <p>{client.routingNo}</p>
                  </div>
                </div>
              </div>
            )}

            {type === "company" && (
              <>
                {/* Client Info Section for Company */}
                {(client.clientName || client.clientEmail) && (
                  <div className="info-section">
                    <h6 className="section-title">Client Info</h6>
                    <div className="row">
                      {client.clientName && (
                        <div className="col-md-4 col-6 info-item">
                          <label>Client Name</label>
                          <p>{client.clientName}</p>
                        </div>
                      )}
                      {client.clientEmail && (
                        <div className="col-md-4 col-6 info-item">
                          <label>Client Email</label>
                          <p>{client.clientEmail}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="info-section">
                  <h6 className="section-title">Payroll & Tax Info</h6>
                  <div className="row">
                    <>
                      <div className="col-md-4 col-6 info-item">
                        <label>Payroll Cycle</label>
                        <p>{client.payrollCycle || "N/A"}</p>
                      </div>
                      <div className="col-md-4 col-6 info-item">
                        <label>Payroll Date</label>
                        <p>{client.payrollDate || "N/A"}</p>
                      </div>
                      <div className="col-md-4 col-6 info-item">
                        <label>Formulas</label>
                        <p>{client.tax || "N/A"}</p>
                      </div>
                    </>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ProfileDetail;
